import React, { useState } from 'react';
import { AppBar as MuiAppBar, Toolbar, Typography, IconButton, ButtonGroup, Button, Box } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import WebhookIcon from '@mui/icons-material/Webhook';
import QuizIcon from '@mui/icons-material/Quiz';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsModal from './SettingsModal';
import HelpPanel from './HelpPanel';
import AuthApiKey from './AuthApiKey';
import AuthLogin from './AuthLogin';
import mbdLogo from '../assets/mbd_full_logo_dark.png';

const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'api_key';
console.log('AUTH_MODE', AUTH_MODE);

const PRODUCTS = ['build', 'deploy', 'eval'];

const ProductBadge = ({ label, active, onClick }) => (
  <Typography
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      fontSize: '0.65rem',
      fontWeight: 600,
      color: 'white',
      letterSpacing: '0.08em',
      alignSelf: 'flex-start',
      lineHeight: 1,
      pt: 0.25,
      border: '1px solid rgba(255, 255, 255, 0.6)',
      borderRadius: '4px',
      px: 0.75,
      py: 0.25,
      cursor: 'pointer',
      bgcolor: active ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
      borderColor: active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)',
      '&:hover': {
        bgcolor: 'rgba(255, 255, 255, 0.1)',
      },
    }}
  >
    {label}
  </Typography>
);

const AppBar = ({ activeProduct, setActiveProduct, activeStep, setActiveStep, steps }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpPanelOpen, setHelpPanelOpen] = useState(false);
  const [authApiKeyOpen, setAuthApiKeyOpen] = useState(false);
  const [authLoginOpen, setAuthLoginOpen] = useState(false);

  const handleSettingsClick = () => {
    setSettingsOpen(true);
  };

  const handleAccountClick = () => {
    if (AUTH_MODE === 'api_key') {
      setAuthApiKeyOpen(true);
    } else if (AUTH_MODE === 'login') {
      setAuthLoginOpen(true);
    }
  };

  return (
    <>
      <MuiAppBar position="static">
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, marginRight: 5 }}>
            <Box
              component="img"
              src={mbdLogo}
              alt="MBD"
              sx={{ height: 32, width: 'auto', display: 'block' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1 }}>
              {PRODUCTS.map((product) => (
                <ProductBadge
                  key={product}
                  label={product}
                  active={activeProduct === product}
                  onClick={() => setActiveProduct(product)}
                />
              ))}
            </Box>
          </Box>

          <ButtonGroup 
            variant="outlined" 
            size="small"
            sx={{
              '& .MuiButton-outlinedInherit': {
                color: 'inherit',
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '& .MuiButton-outlinedInherit.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.5)',
                borderColor: 'rgba(255, 255, 255, 0.25)',
              },
            }}
          >
            {steps.map((step, index) => (
              <Button
                key={index}
                onClick={() => setActiveStep(index)}
                disabled={!step.enabled}
                color="inherit"
                sx={activeStep === index ? {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  borderColor: 'rgba(255, 255, 255, 0.8) !important',
                } : {}}
              >
                {step.label}
              </Button>
            ))}
          </ButtonGroup>

          <IconButton
            color="inherit"
            sx={{ ml: 'auto' }}
            onClick={() => setHelpPanelOpen(true)}
            aria-label="AI help assistant"
          >
            <QuizIcon />
          </IconButton>

          <IconButton
            color="inherit"
            onClick={() => {
              window.open('open_api_specs/', '_blank');
            }}
          >
            <WebhookIcon />
          </IconButton>

          <IconButton
            color="inherit"
            onClick={handleSettingsClick}
            aria-label="settings"
          >
            <SettingsIcon />
          </IconButton>

          <IconButton
            color="inherit"
            aria-label="account"
            onClick={handleAccountClick}
          >
            <AccountCircleIcon />
          </IconButton>
        </Toolbar>
      </MuiAppBar>

      <SettingsModal 
        open={settingsOpen} 
        onClose={() => setSettingsOpen(false)} 
      />

      {AUTH_MODE === 'api_key' && (
        <AuthApiKey
          open={authApiKeyOpen}
          onClose={() => setAuthApiKeyOpen(false)}
        />
      )}

      {AUTH_MODE === 'login' && (
        <AuthLogin
          open={authLoginOpen}
          onClose={() => setAuthLoginOpen(false)}
        />
      )}

      <HelpPanel 
        open={helpPanelOpen} 
        onClose={() => setHelpPanelOpen(false)}
        activeProduct={activeProduct}
        activeStep={activeStep}
        steps={steps}
      />
    </>
  );
};

export default AppBar;
